import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/authApi';
import { tokenStorage } from '../utils/tokenStorage';
import { setAutoLogoutReason } from '../utils/autoLogoutReason';

// NHS DSPT-compliant session timeout constants
const INACTIVITY_TIMEOUT = 15 * 60 * 1000;      // 15 minutes
const ABSOLUTE_TIMEOUT = 12 * 60 * 60 * 1000;   // 12 hours
const WARNING_BEFORE = 2 * 60 * 1000;            // 2-minute warning before timeout
const ACTIVITY_THROTTLE = 30_000;                 // 30s throttle on activity events
const CHECK_INTERVAL = 10_000;                    // 10s check cycle
const HEARTBEAT_INTERVAL = 5 * 60 * 1000;        // 5 minutes

const SESSION_STARTED_KEY = 'clinlexis_session_started_at';
const BROADCAST_CHANNEL_NAME = 'clinlexis-session';

export type WarningType = 'inactivity' | 'absolute';

interface SessionTimeoutState {
  showWarning: boolean;
  timeRemaining: number;
  warningType: WarningType;
  continueSession: () => void;
  signOut: () => void;
  expired: () => void;
}

/**
 * Reads `iat` (issued-at) from the current in-memory JWT.
 * Returns epoch ms, or null if unavailable.
 */
function getSessionStartFromJwt(): number | null {
  const token = tokenStorage.getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.iat) return payload.iat * 1000;
  } catch { /* malformed token */ }
  return null;
}

/**
 * Recovers or initialises `sessionStartedAt`.
 * Priority: sessionStorage > JWT iat > Date.now()
 */
function resolveSessionStartedAt(): number {
  try {
    const stored = sessionStorage.getItem(SESSION_STARTED_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
  } catch { /* sessionStorage unavailable */ }

  const fromJwt = getSessionStartFromJwt();
  const value = fromJwt ?? Date.now();

  try {
    sessionStorage.setItem(SESSION_STARTED_KEY, String(value));
  } catch { /* noop */ }

  return value;
}

export function useSessionTimeout(): SessionTimeoutState {
  const { logout, isImpersonating, user } = useAuth();
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [warningType, setWarningType] = useState<WarningType>('inactivity');

  const lastActivityRef = useRef(Date.now());
  const sessionStartedAtRef = useRef(resolveSessionStartedAt());
  const lastHeartbeatRef = useRef(Date.now());
  const lastThrottledRef = useRef(0);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const warningDeadlineRef = useRef(0);
  const isLoggingOutRef = useRef(false);

  const stopCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const startCountdown = useCallback((deadlineMs: number) => {
    stopCountdown();
    warningDeadlineRef.current = deadlineMs;
    setTimeRemaining(Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000)));

    countdownIntervalRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((warningDeadlineRef.current - Date.now()) / 1000));
      setTimeRemaining(remaining);
    }, 1000);
  }, [stopCountdown]);

  const doLogout = useCallback((reason?: 'inactivity' | 'absolute') => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    stopCountdown();

    // Store the auto-logout reason so the sign-in page can display it.
    // Uses a module-level cache + sessionStorage so the value survives both
    // StrictMode's double-mount in dev and a full page reload.
    if (reason) {
      setAutoLogoutReason(reason);
    }

    // Broadcast logout to other tabs
    try {
      broadcastChannelRef.current?.postMessage({ type: 'logout', reason });
    } catch { /* channel may be closed */ }

    // Clear session start from sessionStorage
    try {
      sessionStorage.removeItem(SESSION_STARTED_KEY);
    } catch { /* noop */ }

    setShowWarning(false);
    logout({ preserveAutoLogoutReason: true });
  }, [logout, stopCountdown]);

  const continueSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    stopCountdown();
    setShowWarning(false);

    // Broadcast activity reset to other tabs
    try {
      broadcastChannelRef.current?.postMessage({ type: 'activity' });
    } catch { /* channel may be closed */ }

    // Send immediate heartbeat to update server-side lastActivityAt
    authApi.heartbeat().catch(() => {});
    lastHeartbeatRef.current = Date.now();
  }, []);

  const signOut = useCallback(() => {
    doLogout();
  }, [doLogout]);

  // Called when the warning countdown reaches zero (auto-expiry, not manual sign-out)
  const expired = useCallback(() => {
    doLogout(warningType);
  }, [doLogout, warningType]);

  // Throttled activity handler
  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottledRef.current < ACTIVITY_THROTTLE) return;
    lastThrottledRef.current = now;
    lastActivityRef.current = now;

    // If warning is showing due to inactivity, dismiss it
    setShowWarning((prev) => {
      if (prev) {
        // Only auto-dismiss for inactivity warnings, not absolute
        setWarningType((type) => {
          if (type === 'inactivity') setShowWarning(false);
          return type;
        });
      }
      return prev;
    });

    // Broadcast activity to other tabs
    try {
      broadcastChannelRef.current?.postMessage({ type: 'activity' });
    } catch { /* channel may be closed */ }
  }, []);

  useEffect(() => {
    isLoggingOutRef.current = false;

    // --- Activity event listeners ---
    const events: Array<keyof DocumentEventMap> = ['mousedown', 'keydown', 'scroll', 'touchstart', 'pointerdown'];
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // --- BroadcastChannel for cross-tab coordination ---
    try {
      const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      broadcastChannelRef.current = channel;

      channel.onmessage = (event) => {
        const { type } = event.data ?? {};
        if (type === 'activity') {
          lastActivityRef.current = Date.now();
        } else if (type === 'logout') {
          // Skip if we've already torn down — guards against the originating
          // tab re-processing its own broadcast and looping back into logout().
          if (isLoggingOutRef.current || !userRef.current) {
            isLoggingOutRef.current = true;
            return;
          }
          isLoggingOutRef.current = true;
          setShowWarning(false);
          const { reason } = event.data ?? {};
          if (reason) {
            setAutoLogoutReason(reason);
          }
          try { sessionStorage.removeItem(SESSION_STARTED_KEY); } catch { /* noop */ }
          logout({ preserveAutoLogoutReason: !!reason });
        }
      };
    } catch {
      // BroadcastChannel not supported — single-tab fallback is fine
    }

    // --- Visibility change handler (laptop sleep/wake) ---
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const now = Date.now();
      const inactiveMs = now - lastActivityRef.current;
      const sessionMs = now - sessionStartedAtRef.current;

      // Past timeout — force logout immediately (no warning)
      if (inactiveMs >= INACTIVITY_TIMEOUT && !isImpersonating) {
        doLogout('inactivity');
        return;
      }
      if (sessionMs >= ABSOLUTE_TIMEOUT) {
        doLogout('absolute');
        return;
      }

      // Within warning window — show warning with correct remaining time
      if (!isImpersonating && inactiveMs >= INACTIVITY_TIMEOUT - WARNING_BEFORE) {
        const deadline = lastActivityRef.current + INACTIVITY_TIMEOUT;
        setWarningType('inactivity');
        setShowWarning(true);
        startCountdown(deadline);
      } else if (sessionMs >= ABSOLUTE_TIMEOUT - WARNING_BEFORE) {
        const deadline = sessionStartedAtRef.current + ABSOLUTE_TIMEOUT;
        setWarningType('absolute');
        setShowWarning(true);
        startCountdown(deadline);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // --- Main check loop ---
    checkIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const inactiveMs = now - lastActivityRef.current;
      const sessionMs = now - sessionStartedAtRef.current;

      // Check absolute session limit first (applies to all sessions including impersonation)
      if (sessionMs >= ABSOLUTE_TIMEOUT) {
        doLogout('absolute');
        return;
      }

      // Check inactivity timeout (skip for impersonation sessions)
      if (!isImpersonating && inactiveMs >= INACTIVITY_TIMEOUT) {
        doLogout('inactivity');
        return;
      }

      // Check absolute warning window
      if (sessionMs >= ABSOLUTE_TIMEOUT - WARNING_BEFORE) {
        if (!countdownIntervalRef.current) {
          const deadline = sessionStartedAtRef.current + ABSOLUTE_TIMEOUT;
          setWarningType('absolute');
          setShowWarning(true);
          startCountdown(deadline);
        }
        return;
      }

      // Check inactivity warning window (skip for impersonation)
      if (!isImpersonating && inactiveMs >= INACTIVITY_TIMEOUT - WARNING_BEFORE) {
        if (!countdownIntervalRef.current) {
          const deadline = lastActivityRef.current + INACTIVITY_TIMEOUT;
          setWarningType('inactivity');
          setShowWarning(true);
          startCountdown(deadline);
        }
        return;
      }

      // No warning needed
      if (countdownIntervalRef.current) stopCountdown();
      setShowWarning(false);
    }, CHECK_INTERVAL);

    // --- Activity-based heartbeat ---
    heartbeatIntervalRef.current = setInterval(() => {
      // Only send heartbeat if user was active since last heartbeat
      if (lastActivityRef.current > lastHeartbeatRef.current) {
        authApi.heartbeat().catch(() => {});
        lastHeartbeatRef.current = Date.now();
      }
    }, HEARTBEAT_INTERVAL);

    // --- Cleanup ---
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);

      try {
        broadcastChannelRef.current?.close();
      } catch { /* noop */ }
    };
  }, [handleActivity, doLogout, logout, isImpersonating]);

  return { showWarning, timeRemaining, warningType, continueSession, signOut, expired };
}
